import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { CronogramaAmortizacao } from '../../../../types/ativoMaster';
import { getTipoColor } from '../../../../utils/cronogramaColors';

// ─── Constants ───────────────────────────────────────────────────────────────

const PALETTE = [
  '#1967D2', '#E51739', '#29A152', '#F5A623', '#7B5EA7',
  '#2C91AD', '#E65100', '#00695C', '#AD1457', '#558B2F',
  '#6D4C41', '#0288D1', '#C62828', '#2E7D32', '#6A1B9A',
];

const DIAS_SEMANA_SHORT = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

// ─── Types / Helpers ─────────────────────────────────────────────────────────

interface AtivoDetail {
  nome:          string;
  classeDisplay?: string;
  subclasse?:    string;
}

interface DayEvent {
  masterId:      number;
  nome:          string;
  classeDisplay?: string;
  subclasse?:    string;
  tipoDisplay:   string;
  tipo:          string;
  color:         string;
}

interface Props {
  cronogramaMap:  { [masterId: number]: CronogramaAmortizacao[] };
  fechadoNomes:   { [masterId: number]: string };
  fechadoDetails: { [masterId: number]: AtivoDetail };
}

const parseDate = (s: string): Date => {
  const p = s.split('/');
  return new Date(parseInt(p[2], 10), parseInt(p[1], 10) - 1, parseInt(p[0], 10));
};

const toKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ─── Component ───────────────────────────────────────────────────────────────

const CronogramaCalendarView: React.FC<Props> = ({ cronogramaMap, fechadoNomes, fechadoDetails }) => {
  const hoje = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [selectedYear, setSelectedYear] = useState<number>(hoje.getFullYear());
  const [calView, setCalView] = useState<{ year: number; month: number }>({
    year: hoje.getFullYear(), month: hoje.getMonth(),
  });

  const prevCalMonth = () => setCalView(v =>
    v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 }
  );
  const nextCalMonth = () => setCalView(v =>
    v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 }
  );
  const goToToday   = () => setCalView({ year: hoje.getFullYear(), month: hoje.getMonth() });

  const colorMap = useMemo(() => {
    const m: { [k: number]: string } = {};
    Object.keys(cronogramaMap).map(Number).forEach((id, i) => { m[id] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [cronogramaMap]);

  const allEventMap = useMemo(() => {
    const m: { [k: string]: DayEvent[] } = {};
    Object.keys(cronogramaMap).forEach(idStr => {
      const masterId      = Number(idStr);
      const detail        = fechadoDetails[masterId];
      const nome          = detail?.nome ?? fechadoNomes[masterId] ?? `Ativo ${masterId}`;
      const classeDisplay = detail?.classeDisplay;
      const subclasse     = detail?.subclasse;
      const color         = colorMap[masterId];
      cronogramaMap[masterId].forEach(cron => {
        cron.datas_geradas.forEach(({ data }) => {
          const key = toKey(parseDate(data));
          if (!m[key]) m[key] = [];
          if (!m[key].some(e => e.masterId === masterId && e.tipo === cron.tipo)) {
            m[key].push({ masterId, nome, classeDisplay, subclasse, tipoDisplay: cron.tipo_display, tipo: cron.tipo, color });
          }
        });
      });
    });
    return m;
  }, [cronogramaMap, fechadoNomes, fechadoDetails, colorMap]);

  const thisMonthCount = useMemo(() => {
    const start = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const end   = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    let total = 0;
    Object.keys(allEventMap).forEach(key => {
      const d = new Date(key + 'T00:00:00');
      if (d >= start && d <= end) total += allEventMap[key].length;
    });
    return total;
  }, [allEventMap, hoje]);

  const nextMonthCount = useMemo(() => {
    const start = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
    const end   = new Date(hoje.getFullYear(), hoje.getMonth() + 2, 0);
    let total = 0;
    Object.keys(allEventMap).forEach(key => {
      const d = new Date(key + 'T00:00:00');
      if (d >= start && d <= end) total += allEventMap[key].length;
    });
    return total;
  }, [allEventMap, hoje]);
  const totalAtivos    = Object.keys(cronogramaMap).length;

  const availableYears = useMemo(() => {
    const yearsObj: { [y: number]: boolean } = {};
    Object.keys(allEventMap).forEach(key => {
      const d = new Date(key + 'T00:00:00');
      if (d >= hoje) yearsObj[d.getFullYear()] = true;
    });
    return Object.keys(yearsObj).map(Number).sort();
  }, [allEventMap, hoje]);

  const miniCalDays = useMemo(() => {
    const { year: y, month: m } = calView;
    const firstWD = new Date(y, m, 1).getDay();
    const daysInM = new Date(y, m + 1, 0).getDate();
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstWD; i++) cells.push(null);
    for (let d = 1; d <= daysInM; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calView]);

  const getMiniEvents = (day: number | null): DayEvent[] => {
    if (!day) return [];
    return allEventMap[toKey(new Date(calView.year, calView.month, day))] ?? [];
  };

  const groupedUpcoming = useMemo(() => {
    const groups: { monthLabel: string; monthKey: string; days: { date: Date; key: string; items: DayEvent[] }[] }[] = [];
    const idxMap: { [mk: string]: number } = {};
    Object.keys(allEventMap)
      .filter(key => new Date(key + 'T00:00:00') >= hoje)
      .sort()
      .forEach(key => {
        const d = new Date(key + 'T00:00:00');
        const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (idxMap[mk] === undefined) {
          idxMap[mk] = groups.length;
          groups.push({ monthKey: mk, monthLabel: `${MESES[d.getMonth()]} ${d.getFullYear()}`, days: [] });
        }
        groups[idxMap[mk]].days.push({ date: d, key, items: allEventMap[key] });
      });
    return groups;
  }, [allEventMap, hoje]);

  const groupedForYear = useMemo(() =>
    groupedUpcoming.filter(g => g.monthKey.startsWith(`${selectedYear}-`))
  , [groupedUpcoming, selectedYear]);

  const yearTotalCount = groupedForYear.reduce((s, g) => s + g.days.reduce((ss, d) => ss + d.items.length, 0), 0);

  const prevYearAvail = availableYears.indexOf(selectedYear) > 0;
  const nextYearAvail = availableYears.indexOf(selectedYear) < availableYears.length - 1;

  const legendItems = useMemo(() =>
    Object.keys(cronogramaMap).map(Number).map(id => {
      const detail = fechadoDetails[id];
      return {
        id,
        nome:          detail?.nome ?? fechadoNomes[id] ?? `Ativo ${id}`,
        classeDisplay: detail?.classeDisplay,
        subclasse:     detail?.subclasse,
        color:         colorMap[id],
      };
    })
  , [cronogramaMap, fechadoNomes, fechadoDetails, colorMap]);

  const tipoBadgeSx = (tipo: string) => {
    const s = getTipoColor(tipo);
    return { fontSize: '10px', height: 18, fontFamily: 'Inter', fontWeight: 600, backgroundColor: s.bg, color: s.color };
  };

  const navBtnSx = (enabled: boolean) => ({
    width: 28, height: 28, fontSize: '16px', fontWeight: 700,
    color: enabled ? '#1E293B' : '#CBD5E1',
    border: `1px solid ${enabled ? '#E2E8F0' : '#F1F5F9'}`,
    borderRadius: 1,
    '&:hover': { backgroundColor: enabled ? '#F1F5F9' : 'transparent' },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 560, p: 2 }}>

      {/* ── Cards de resumo ── */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2.5, flexShrink: 0 }}>
        {[
          { label: 'Ativos com cronograma', value: totalAtivos,    color: '#1D4ED8', bg: '#EFF6FF' },
          { label: `Pagamentos em ${MESES[hoje.getMonth()].slice(0,3)}`, value: thisMonthCount, color: '#15803D', bg: '#F0FDF4' },
          { label: `Pagamentos em ${MESES[(hoje.getMonth()+1)%12].slice(0,3)}`, value: nextMonthCount, color: '#B45309', bg: '#FFFBEB' },
        ].map(c => (
          <Box key={c.label} sx={{
            px: 2, py: 1.25, borderRadius: 2,
            backgroundColor: c.bg, border: `1px solid ${c.bg}`,
            minWidth: 160,
          }}>
            <Typography sx={{ fontSize: '11px', color: '#64748B', fontFamily: 'Inter', mb: 0.25 }}>
              {c.label}
            </Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: c.color, fontFamily: 'Inter', lineHeight: 1 }}>
              {c.value}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* ── Conteúdo principal ── */}
      <Stack direction="row" spacing={0} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* ── Coluna esquerda: mini-calendário + legenda ── */}
        <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, pr: 3 }}>

          {/* Mini-calendário — altura fixa */}
          <Box sx={{ flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <IconButton size="small" onClick={prevCalMonth} sx={navBtnSx(true)}>‹</IconButton>
              <Stack alignItems="center" spacing={0}>
                <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '13px', color: '#1E293B', lineHeight: 1 }}>
                  {MESES[calView.month]} {calView.year}
                </Typography>
                {(calView.year !== hoje.getFullYear() || calView.month !== hoje.getMonth()) && (
                  <Typography
                    onClick={goToToday}
                    sx={{ fontSize: '10px', color: '#1D4ED8', fontFamily: 'Inter', cursor: 'pointer', mt: 0.25,
                      '&:hover': { textDecoration: 'underline' } }}
                  >
                    Hoje
                  </Typography>
                )}
              </Stack>
              <IconButton size="small" onClick={nextCalMonth} sx={navBtnSx(true)}>›</IconButton>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
              {DIAS_SEMANA_SHORT.map((d, i) => (
                <Typography key={i} sx={{
                  textAlign: 'center', fontSize: '10px', fontWeight: 600,
                  color: '#94A3B8', fontFamily: 'Inter',
                }}>
                  {d}
                </Typography>
              ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
              {miniCalDays.map((day, idx) => {
                const events  = getMiniEvents(day);
                const isToday = !!day && new Date(calView.year, calView.month, day).getTime() === hoje.getTime();
                const isPast  = !!day && new Date(calView.year, calView.month, day) < hoje;
                return (
                  <Box key={idx} sx={{
                    minHeight: 34, p: '2px', borderRadius: 0.75,
                    backgroundColor: isToday ? '#EFF6FF' : 'transparent',
                    border: isToday ? '1px solid #BFDBFE' : '1px solid transparent',
                    opacity: isPast ? 0.4 : 1,
                  }}>
                    {day && (
                      <>
                        <Typography sx={{
                          fontSize: '11px', textAlign: 'center', fontFamily: 'Inter',
                          fontWeight: isToday ? 700 : 400,
                          color: isToday ? '#1D4ED8' : '#374151',
                          lineHeight: 1.4,
                        }}>
                          {day}
                        </Typography>
                        {events.length > 0 && (
                          <Stack direction="row" gap="2px" justifyContent="center" flexWrap="wrap">
                            {events.slice(0, 3).map((ev, i) => (
                              <Tooltip
                                key={i}
                                title={[ev.nome, ev.classeDisplay, ev.subclasse].filter(Boolean).join(' · ') + ` · ${ev.tipoDisplay}`}
                                placement="top"
                                arrow
                              >
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: ev.color, cursor: 'default' }} />
                              </Tooltip>
                            ))}
                            {events.length > 3 && (
                              <Typography sx={{ fontSize: '8px', color: '#94A3B8', lineHeight: '6px' }}>
                                +{events.length - 3}
                              </Typography>
                            )}
                          </Stack>
                        )}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Legenda — ocupa o espaço restante e scrolla se necessário */}
          <Box sx={{ flex: 1, minHeight: 0, mt: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 700, fontSize: '12px', color: '#64748B',
              textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, flexShrink: 0,
            }}>
              Ativos
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Stack spacing={0.75}>
                {legendItems.map(item => (
                  <Stack key={item.id} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0, mt: '3px' }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: '12px', fontFamily: 'Inter', color: '#374151',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {item.nome}
                      </Typography>
                      {(item.classeDisplay || item.subclasse) && (
                        <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: '#94A3B8', lineHeight: 1.3 }}>
                          {[item.classeDisplay, item.subclasse].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* ── Coluna direita: navegação por ano + lista ── */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', pl: 3 }}>

          {/* Navegação por ano — altura fixa */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexShrink: 0 }}>
            <IconButton
              size="small"
              disabled={!prevYearAvail}
              onClick={() => setSelectedYear(y => y - 1)}
              sx={navBtnSx(prevYearAvail)}
            >
              ‹
            </IconButton>

            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '16px', color: '#1E293B', lineHeight: 1 }}>
                {selectedYear}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#94A3B8', mt: 0.25 }}>
                {yearTotalCount} pagamento{yearTotalCount !== 1 ? 's' : ''}
              </Typography>
            </Box>

            <IconButton
              size="small"
              disabled={!nextYearAvail}
              onClick={() => setSelectedYear(y => y + 1)}
              sx={navBtnSx(nextYearAvail)}
            >
              ›
            </IconButton>
          </Stack>

          {/* Lista — ocupa o espaço restante e scrolla se necessário */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
            {groupedForYear.length === 0 ? (
              <Typography sx={{ color: '#94A3B8', fontSize: '13px', fontFamily: 'Inter', fontStyle: 'italic', py: 2 }}>
                Nenhum pagamento em {selectedYear}.
              </Typography>
            ) : (
              <Stack spacing={2.5}>
                {groupedForYear.map(group => (
                  <Box key={group.monthKey}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                      <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '13px', color: '#1E293B' }}>
                        {group.monthLabel}
                      </Typography>
                      <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, backgroundColor: '#F1F5F9', border: '1px solid #E2E8F0' }}>
                        <Typography sx={{ fontSize: '11px', color: '#64748B', fontFamily: 'Inter', fontWeight: 600 }}>
                          {group.days.reduce((s, d) => s + d.items.length, 0)} pagamentos
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack spacing={0.5}>
                      {group.days.map(({ date, key, items }) => {
                        const isToday = key === toKey(hoje);
                        return (
                          <Stack key={key} direction="row" spacing={1.25} alignItems="flex-start">
                            <Box sx={{
                              flexShrink: 0, width: 40, textAlign: 'center',
                              px: 0.5, py: 0.25, borderRadius: 1,
                              backgroundColor: isToday ? '#EFF6FF' : '#F8FAFC',
                              border: `1px solid ${isToday ? '#BFDBFE' : '#E2E8F0'}`,
                            }}>
                              <Typography sx={{
                                fontSize: '14px', fontWeight: 700, fontFamily: 'Inter',
                                lineHeight: 1.2, color: isToday ? '#1D4ED8' : '#1E293B',
                              }}>
                                {String(date.getDate()).padStart(2, '0')}
                              </Typography>
                              <Typography sx={{
                                fontSize: '9px', color: '#94A3B8', fontFamily: 'Inter',
                                textTransform: 'uppercase', letterSpacing: '0.4px',
                              }}>
                                {MESES[date.getMonth()].slice(0, 3)}
                              </Typography>
                            </Box>

                            <Stack spacing={0.25} sx={{ flex: 1, pt: 0.1 }}>
                              {items.map((ev, i) => (
                                <Stack key={i} direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: ev.color, flexShrink: 0 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{
                                      fontSize: '12px', fontFamily: 'Inter', color: '#374151',
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                      {ev.nome}
                                    </Typography>
                                    {(ev.classeDisplay || ev.subclasse) && (
                                      <Typography sx={{ fontSize: '10px', fontFamily: 'Inter', color: '#94A3B8', lineHeight: 1.2 }}>
                                        {[ev.classeDisplay, ev.subclasse].filter(Boolean).join(' · ')}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip label={ev.tipoDisplay} size="small" sx={tipoBadgeSx(ev.tipo)} />
                                </Stack>
                              ))}
                            </Stack>
                          </Stack>
                        );
                      })}
                    </Stack>

                    <Divider sx={{ mt: 1.25 }} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Box>

      </Stack>
    </Box>
  );
};

export default CronogramaCalendarView;
